import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { API_URL } from '../../config';
import {
  Search,
  FileText,
  Loader2,
  ArrowRight,
  Command,
} from 'lucide-react';

const PublicSearchModal = ({ isOpen, onClose, spaceId, spaceName, onSelectResult }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Debounced search
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/public/search`, {
        params: { 
          q: searchQuery, 
          space_id: spaceId,
          limit: 20 
        }
      });
      setResults(response.data.results || []);
      setSelectedIndex(0);
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [spaceId]);

  // Handle input change with debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, performSearch]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleSelect = (result) => {
    onClose();
    // Pass the result with search query for highlighting
    onSelectResult({
      ...result,
      searchQuery: query
    });
  };

  // Highlight matching text in results
  const highlightMatch = (text, searchQuery) => {
    if (!searchQuery || !text) return text;
    
    try {
      const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      const parts = text.split(regex);
      
      return parts.map((part, i) => 
        regex.test(part) 
          ? <mark key={i} className="bg-yellow-200 dark:bg-yellow-700/50 text-inherit rounded px-0.5">{part}</mark>
          : part
      );
    } catch {
      return text;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-2xl p-0 gap-0 overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700" 
        data-testid="public-search-modal"
        aria-describedby={undefined}
      >
        <VisuallyHidden.Root asChild>
          <DialogTitle>Search Documentation</DialogTitle>
        </VisuallyHidden.Root>
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <Search className="w-5 h-5 text-slate-400" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Search in ${spaceName || 'documentation'}...`}
            className="border-0 shadow-none focus-visible:ring-0 text-lg placeholder:text-slate-400 bg-transparent"
            data-testid="public-search-input"
          />
          {loading && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
          <kbd className="hidden sm:inline-flex items-center px-2 py-1 text-xs font-mono bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded text-slate-500">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <ScrollArea className="max-h-[400px]">
          {query.length < 2 ? (
            <div className="p-8 text-center text-slate-500">
              <Command className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
              <p className="text-sm font-medium">Search documentation</p>
              <p className="text-xs text-slate-400 mt-2">
                Type at least 2 characters to search
              </p>
              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-400">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded">↑</kbd>
                  <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded">↓</kbd>
                  to navigate
                </span>
                <span className="text-slate-300 dark:text-slate-600">•</span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded">Enter</kbd>
                  to select
                </span>
              </div>
            </div>
          ) : results.length === 0 && !loading ? (
            <div className="p-8 text-center text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
              <p className="text-sm font-medium">No results found</p>
              <p className="text-xs text-slate-400 mt-1">
                No documents match "{query}"
              </p>
            </div>
          ) : (
            <div className="py-2">
              {results.map((result, index) => (
                <button
                  key={result.id}
                  onClick={() => handleSelect(result)}
                  className={`w-full px-4 py-3 text-left flex items-start gap-3 transition-colors ${
                    index === selectedIndex
                      ? 'bg-blue-50 dark:bg-blue-900/20'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                  }`}
                  data-testid={`public-search-result-${index}`}
                >
                  <div className={`mt-0.5 p-1.5 rounded ${
                    index === selectedIndex
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-500'
                  }`}>
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-slate-900 dark:text-white truncate">
                        {highlightMatch(result.title, query)}
                      </span>
                      {result.title?.toLowerCase().includes(query.toLowerCase()) && (
                        <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                          Title match
                        </Badge>
                      )}
                    </div>
                    {result.content_snippet && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                        {highlightMatch(result.content_snippet, query)}
                      </p>
                    )}
                  </div>
                  <ArrowRight className={`w-4 h-4 mt-2 transition-opacity ${
                    index === selectedIndex ? 'opacity-100 text-blue-600 dark:text-blue-400' : 'opacity-0'
                  }`} />
                </button>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between text-xs text-slate-500">
          <span>
            {results.length > 0 ? `${results.length} result${results.length !== 1 ? 's' : ''}` : 'Start typing to search'}
          </span>
          <div className="hidden sm:flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded">⌘</kbd>
              <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded">K</kbd>
              to open
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PublicSearchModal;
